import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { registerUser } from "../../store/slices/authentificationSlice";
import { User, Lock, Mail, CheckCircle } from "lucide-react";

const Register: React.FC = () => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state: any) => state.authentification);

  const [formData, setFormData] = useState({
    name: "",
    first_name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [validationError, setValidationError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (validationError) setValidationError(null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setValidationError("Les mots de passe ne correspondent pas");
      return;
    }
    if (formData.password.length < 6) {
      setValidationError("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    try {
      const { confirmPassword, ...userData } = formData;
      await dispatch(registerUser(userData) as any).unwrap();
      setSuccess(true);
    } catch (err) {
      console.error("Erreur d'inscription:", err);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-10 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Vérifiez votre boîte mail !</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Un email vous a été envoyé. Cliquez sur le lien dans le mail pour <strong>activer votre compte</strong>.
          </p>
          <Link
            to="/signin"
            className="inline-block px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
          >
            Retour à la connexion
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-white dark:bg-gray-900">
      {/* Formulaire */}
      <div className="flex flex-col justify-center w-full lg:w-1/2 px-6 sm:px-8 lg:px-16 py-12">
        <div className="w-full max-w-md mx-auto">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Créer un compte</h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Rejoignez-nous dès maintenant</p>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit}>
            {(validationError || error) && (
              <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
                <p className="text-sm text-red-800 dark:text-red-400">{validationError || error}</p>
              </div>
            )}

            {/* Nom */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input name="name" type="text" required value={formData.name} onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Nom de famille" />
              </div>
            </div>

            {/* Prénom */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prénom</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input name="first_name" type="text" value={formData.first_name} onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Prénom" />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input name="email" type="email" required autoComplete="email" value={formData.email} onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Adresse email" />
              </div>
            </div>

            {/* Mot de passe */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mot de passe</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input name="password" type="password" required autoComplete="new-password" value={formData.password} onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Minimum 6 caractères" />
              </div>
            </div>

            {/* Confirmer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirmer le mot de passe</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input name="confirmPassword" type="password" required autoComplete="new-password" value={formData.confirmPassword} onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Répéter le mot de passe" />
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full flex justify-center py-2.5 px-4 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {loading ? "Création en cours..." : "S'inscrire"}
            </button>

            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              Vous avez déjà un compte ?{" "}
              <Link to="/signin" className="font-medium text-indigo-600 hover:text-indigo-500">Se connecter</Link>
            </p>
          </form>
        </div>
      </div>

      {/* Branding */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center bg-indigo-600 px-12">
        <div className="max-w-md text-white">
          <div className="mb-6">
            <div className="h-14 w-14 rounded-xl bg-white/10 flex items-center justify-center text-2xl font-bold">A</div>
          </div>
          <h1 className="text-4xl font-bold leading-tight">Gestion immobilière simplifiée</h1>
          <p className="mt-4 text-indigo-100 text-base leading-7">
            Centralisez vos biens, suivez vos locations et gérez votre activité en toute simplicité.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
